import type { WhoamiMe } from "@shared-domain/auth-service/contract";

/**
 * PRODUCT_DEV-748: フロントの権限判定。もとは `components/shared/AdminLayout.tsx` に
 * 同居していたが、**純粋な判定ロジックなのに管理者画面レイアウトを引き連れてくる**
 * のが問題だった（メモ画面や ToDo タブも admin 判定を使うため、Minizo の dist にまで
 * 管理者画面のレイアウトが入ってしまう）。
 *
 * AdminLayout 側にあった `react-refresh/only-export-components` の disable も、
 * 「コンポーネントのファイルから非コンポーネントを export している」ことが原因なので
 * ここへ移すと不要になる。
 *
 * 判定そのものは移動前と同一（改ざんは許容。サーバ側の認可が正）。
 */

/**
 * フロントでのadmin権限チェック（改ざんは許容）
 */
export const checkRoleIsAdmin = (auth: WhoamiMe): boolean => {
  if (!auth || !auth.currentTenantId || !auth.info) {
    return false;
  }
  const tenantId = auth.currentTenantId;
  const productId = auth.info.productId;
  const membership = auth.memberships.find(
    (x) =>
      x.tenantId === tenantId && x.products.some((p) => p.id === productId),
  );
  if (!membership) {
    return false;
  }
  return membership.role === "admin";
};

/**
 * フロントでのsysadmin権限チェック（admin + specialFlags["sysadmin"]）
 */
export const checkRoleIsSysadmin = (auth: WhoamiMe): boolean => {
  if (!checkRoleIsAdmin(auth)) return false;
  const tenantId = auth.currentTenantId;
  const productId = auth.info!.productId;
  const membership = auth.memberships.find(
    (x) =>
      x.tenantId === tenantId && x.products.some((p) => p.id === productId),
  );
  return membership?.specialFlags?.includes("sysadmin") ?? false;
};

/**
 * PRODUCT_DEV-721: **部の集計（管理者ビュー）を見せるか**の出し分け。
 *
 * 9/8 のプロダクト要件会議で権限を2軸に割った。業務の数字を見るのは **manager**（業務軸）で、
 * **admin（システム軸）は設定とデータを管理する人**であって数字を見る人ではない。
 *
 * 例外が1つある。`deptRole` は所属レコードに載るので、**部署を1つも作っていないテナントでは
 * manager を置けない**。そこだけは従来どおり admin に見せる（ユーザ判断 2026-09-10）。
 * サーバ側 `lib/permissions` の `canViewTeamSummary` と同じ切り方にしてある。
 *
 * ⚠ 切り替えは**テナント単位の機能フラグ `managerRole`**（`useTenantFeatureFlag`）。
 * OFF のテナントは今日とまったく同じ見え方でなければならない。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanViewTeamSummary = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; hasDepartment: boolean; roleSplit: boolean },
): boolean => {
  // 機能フラグ `managerRole` が OFF のテナントは従来どおり admin 基準（回帰なし）
  if (!ctx.roleSplit) return checkRoleIsAdmin(auth);
  if (ctx.isManager) return true;
  // 部署が無い（＝未所属枠しかない）テナントでは admin に残す
  return !ctx.hasDepartment && checkRoleIsAdmin(auth);
};

/**
 * PRODUCT_DEV-721: **他人の日報にコメント欄を出すか**の出し分け。
 *
 * 機能フラグ `managerRole` が ON のテナントでは **manager** に出す（admin ではない）。
 * 日報の画面は、サーバが読むことを許した相手のものしか表示されないので、
 * **出し分けは管理者ビューと同じ条件でよい**（`checkCanViewTeamSummary`）。
 * どの部署の manager か、相手が未所属かはサーバ側 `canCommentOnMemberActivity` が見ている。
 *
 * 所属の無い admin にも出す。未所属枠のまとめを見られる人は、そこに並ぶ人の日報も
 * 読めてコメントもできる、にサーバ側を揃えたため（部署を作っていないテナントで
 * 管理者ビューが一覧までしか使えなかった）。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanCommentOnMemberActivity = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; hasDepartment: boolean; roleSplit: boolean },
): boolean => checkCanViewTeamSummary(auth, ctx);

/**
 * PRODUCT_DEV-721 第2期④: **管理者ビューに入れるか**の出し分け。
 *
 * 管理者ビューには、業務の数字（活動サマリ＝manager）と、配信の設定（フォーマットと配信＝admin）が
 * 同居している。フラグ ON では manager でない admin は活動サマリを見られないが、**配信の設定は
 * この画面にしか無い**ので入口は開ける（中は「フォーマットと配信」だけを出す）。
 * 開けないと、部署に所属していて manager でない admin が配信の設定にたどり着けない。
 *
 * OFF のテナントは `checkCanViewTeamSummary` と同じ（admin）。
 * 配信の設定を管理メニューへ移す案があり、移したらこの関数は要らなくなる。
 *
 * ⚠ ここは**表示の出し分けだけ**。改ざんは許容で、サーバ側の認可が正。
 */
export const checkCanOpenAdminView = (
  auth: WhoamiMe,
  ctx: { isManager: boolean; hasDepartment: boolean; roleSplit: boolean },
): boolean =>
  checkCanViewTeamSummary(auth, ctx) || (ctx.roleSplit && checkRoleIsAdmin(auth));
